import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { useMeterStatusThresholds } from "@/shared/hooks/useMeterStatusThresholds";
import { MeterStatusThresholdDraft } from "@/shared/models/MeterStatusThreshold";
import { upsertMeterStatusThresholds } from "@/shared/repositories/MeterStatusThresholdsRepository";
import {
	DEFAULT_METER_STATUS_THRESHOLDS,
	getMeterStatusBands,
	MeterStatusLevel,
	MeterStatusMetric,
} from "@/shared/utils/meterStatusLevels";
import { ErrorModal } from "@dexteel/mesf-core";
import { Box, Button, Divider, Paper, Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { MeterStatusBoundaryFields } from "./MeterStatusBoundaryFields";
import {
	describeMeterStatusBands,
	MeterStatusScale,
	metrics,
} from "./MeterStatusScale";

const STEP = 1;

const sameDrafts = (
	left: MeterStatusThresholdDraft[],
	right: MeterStatusThresholdDraft[],
) =>
	left.length === right.length &&
	left.every(
		(draft, index) =>
			draft.Level === right[index].Level &&
			draft.Threshold === right[index].Threshold,
	);

const moveBoundary = (
	drafts: MeterStatusThresholdDraft[],
	metric: MeterStatusMetric,
	level: MeterStatusLevel,
	value: number,
): MeterStatusThresholdDraft[] => {
	const bands = getMeterStatusBands(drafts, metric);
	const index = bands.findIndex((band) => band.Level === level);
	if (index <= 0) return drafts;

	const max = metrics[metric].scaleMax;
	const boundaries = bands.map((band) => band.Threshold);
	boundaries[index] = Math.max(
		index * STEP,
		Math.min(max - (bands.length - 1 - index) * STEP, value),
	);

	for (let i = index + 1; i < boundaries.length; i++)
		boundaries[i] = Math.max(boundaries[i], boundaries[i - 1] + STEP);

	for (let i = index - 1; i >= 1; i--)
		boundaries[i] = Math.min(boundaries[i], boundaries[i + 1] - STEP);

	const movedByLevel = new Map(
		bands.map((band, position): [MeterStatusLevel, number] => [
			band.Level,
			boundaries[position],
		]),
	);

	return drafts.map((draft) =>
		draft.Metric === metric && movedByLevel.has(draft.Level)
			? {
					...draft,
					Threshold: movedByLevel.get(draft.Level) ?? draft.Threshold,
				}
			: draft,
	);
};

const MetricSection = ({
	drafts,
	metric,
	title,
	description,
	disabled,
	onChange,
}: {
	drafts: MeterStatusThresholdDraft[];
	metric: MeterStatusMetric;
	title: string;
	description: string;
	disabled: boolean;
	onChange: (next: MeterStatusThresholdDraft[]) => void;
}) => {
	const changeBoundary = (level: MeterStatusLevel, value: number) =>
		onChange(moveBoundary(drafts, metric, level, value));

	return (
		<Box>
			<Typography variant="subtitle2" fontWeight={700}>
				{title}
			</Typography>
			<Typography
				component="div"
				variant="caption"
				color="text.secondary"
				sx={{ display: "block", pb: 1 }}
			>
				{description}
			</Typography>

			<MeterStatusScale
				drafts={drafts}
				metric={metric}
				disabled={disabled}
				onBoundaryChange={changeBoundary}
			/>

			<Box sx={{ pt: 1.5 }}>
				<MeterStatusBoundaryFields
					drafts={drafts}
					metric={metric}
					max={metrics[metric].scaleMax}
					disabled={disabled}
					onChange={changeBoundary}
				/>
			</Box>

			<Typography
				component="div"
				variant="caption"
				color="text.secondary"
				sx={{ display: "block", pt: 1 }}
			>
				{describeMeterStatusBands(drafts, metric)}
			</Typography>
		</Box>
	);
};

export const WaterMeterThresholdsForm = () => {
	const queryClient = useQueryClient();

	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [drafts, setDrafts] = useState<MeterStatusThresholdDraft[]>(
		DEFAULT_METER_STATUS_THRESHOLDS,
	);

	const {
		data: saved,
		drafts: savedDrafts,
		error: loadError,
		isLoading,
	} = useMeterStatusThresholds();

	const saveMutation = useMutation(upsertMeterStatusThresholds, {
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["meterStatusThresholds"],
			});
			setNotification("Thresholds saved");
		},
		onError: (e: Error) => setError(e.message),
	});

	const isDirty = !sameDrafts(drafts, savedDrafts);
	const isBusy = saveMutation.isPending;

	useEffect(() => {
		if (saved) {
			setDrafts(savedDrafts);
		}
	}, [saved]);

	if (isLoading)
		return (
			<Box sx={{ py: 4 }}>
				<CenteredLazyLoading />
			</Box>
		);

	return (
		<>
			<Paper elevation={2} sx={{ p: 1.5 }}>
				<Stack spacing={1.5}>
					{loadError && (
						<Typography variant="body2" color="error">
							{`Showing the built-in defaults: ${loadError.message}`}
						</Typography>
					)}

					<MetricSection
						drafts={drafts}
						metric="Lag"
						title="Reporting lag"
						description="How long a meter may go without a reading before the table flags it."
						disabled={isBusy}
						onChange={setDrafts}
					/>

					<Divider />

					<MetricSection
						drafts={drafts}
						metric="Coverage"
						title="Period coverage"
						description="How many of the readings expected over the period on screen arrived."
						disabled={isBusy}
						onChange={setDrafts}
					/>

					<Stack direction="row" spacing={1} justifyContent="flex-end">
						<Button
							size="small"
							onClick={() => setDrafts(savedDrafts)}
							disabled={isBusy || !isDirty}
						>
							Cancel
						</Button>
						<Button
							size="small"
							variant="contained"
							onClick={() => saveMutation.mutate(drafts)}
							disabled={isBusy || !isDirty}
						>
							Save
						</Button>
					</Stack>
				</Stack>
			</Paper>

			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
		</>
	);
};
